// Modern Header component with unified navigation
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMenu, FiX, FiPhone, FiChevronDown } from 'react-icons/fi';
import styles from './Header.module.css';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isProgramDropdownOpen, setIsProgramDropdownOpen] = useState(false);
  const [isBranchDropdownOpen, setIsBranchDropdownOpen] = useState(false);
  const location = useLocation();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setIsProgramDropdownOpen(false);
    setIsBranchDropdownOpen(false);
  }, [location]);

  const navigationItems = [
    { label: 'Home', path: '/' },
    { label: 'Students Work', path: '/students-work' },
    { label: 'Placements', path: '/placements' },
    { label: 'About Us', path: '/about' },
    { label: 'Online Learning', path: '/online-learning' },
    { label: 'Blog', path: '/blog' },
    { label: 'Contact', path: '/contact' },
  ];

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const programs = [
    { name: 'Graphic & UI/UX Design', path: '/courses/advanced-graphic-and-uiux-design' },
    { name: 'Motion Graphic, Video Editing & VFX', path: '/courses/advanced-motion-graphic-and-video-editing' },
    { name: '3D Animation & Modeling', path: '/courses/advanced-3d-animation-and-modeling' },
    { name: 'Interior Design', path: '/courses/advanced-interior-design' },
    { name: '3D Animation & VFX', path: '/courses/advanced-3d-animation-and-vfx' },
    { name: 'Web Design & Development', path: '/courses/advanced-web-design-and-development' },
    { name: 'Digital Marketing', path: '/courses/advanced-digital-marketing' },
    { name: 'Multimedia Animation & VFX', path: '/courses/advanced-multimedia-animation-and-vfx' },
  ];

  const branches = [
    { name: 'Goregaon', phone: '+91 9819508524' },
    { name: 'Malad', phone: '+91 9223439986' },
    { name: 'Mulund', phone: '+91 9619177392' },
    { name: 'Nalasopara', phone: '+91 7021382816' },
    { name: 'Santacruz', phone: '+91 7304157986' },
  ];

  return (
    <header className={`${styles.header} ${isScrolled ? styles.scrolled : ''}`}>
      <nav className={styles.nav}>
        <div className="container">
          <div className={styles.navContent}>
            {/* Logo */}
            <div className={styles.logo}>
              <Link to="/">
                <img
                  src="/images/logowhite.webp"
                  alt="Shankar Multimedia"
                  className={styles.logoImg}
                  decoding="async"
                />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className={styles.desktopNav}>
              {navigationItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={scrollToTop}
                  className={`${styles.navLink} ${
                    location.pathname === item.path ? styles.active : ''
                  }`}
                >
                  {item.label}
                </Link>
              ))}

              {/* Programs Dropdown */}
              <div
                className={styles.programDropdown}
                onMouseEnter={() => setIsProgramDropdownOpen(true)}
                onMouseLeave={() => setIsProgramDropdownOpen(false)}
              >
                <button
                  type="button"
                  className={styles.programToggle}
                  onClick={() => setIsProgramDropdownOpen((prev) => !prev)}
                  aria-haspopup="true"
                  aria-expanded={isProgramDropdownOpen}
                >
                  <span>Programs</span>
                  <FiChevronDown
                    className={`${styles.chevron} ${
                      isProgramDropdownOpen ? styles.chevronOpen : ''
                    }`}
                  />
                </button>

                <AnimatePresence>
                  {isProgramDropdownOpen && (
                    <motion.div
                      className={styles.programDropdownMenu}
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                    >
                      {programs.map((program) => (
                        <Link
                          key={program.path}
                          to={program.path}
                          onClick={scrollToTop}
                          className={styles.programItem}
                        >
                          <span className={styles.programName}>{program.name}</span>
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Branch Dropdown */}
              <div
                className={styles.branchDropdown}
                onMouseEnter={() => setIsBranchDropdownOpen(true)}
                onMouseLeave={() => setIsBranchDropdownOpen(false)}
              >
                <button className={styles.branchToggle}>
                  <FiPhone className={styles.phoneIcon} />
                  <span>Call Us</span>
                  <FiChevronDown
                    className={`${styles.chevron} ${
                      isBranchDropdownOpen ? styles.chevronOpen : ''
                    }`}
                  />
                </button>

                <AnimatePresence>
                  {isBranchDropdownOpen && (
                    <motion.div
                      className={styles.branchDropdownMenu}
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                    >
                      {branches.map((branch) => (
                        <a
                          key={branch.name}
                          href={`tel:${branch.phone.replace(/\s/g, '')}`}
                          className={styles.branchItem}
                        >
                          <span className={styles.branchName}>{branch.name}</span>
                          <span className={styles.branchPhone}>{branch.phone}</span>
                        </a>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Mobile Menu Toggle */}
            <button
              className={styles.mobileToggle}
              onClick={toggleMenu}
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? <FiX /> : <FiMenu />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation */}
      <motion.div
        className={`${styles.mobileNav} ${isMenuOpen ? styles.mobileNavOpen : ''}`}
        initial={false}
        animate={{
          height: isMenuOpen ? 'auto' : 0,
          opacity: isMenuOpen ? 1 : 0,
        }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
      >
        <div className={styles.mobileNavContent}>
          {navigationItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={scrollToTop}
              className={
                item.label === 'Contact'
                  ? `${styles.mobileNavLink} ${styles.mobileNavLinkContact}`
                  : styles.mobileNavLink
              }
            >
              {item.label}
            </Link>
          ))}

          {/* Mobile Programs */}
          <div className={styles.mobileProgramSection}>
            <h4 className={styles.mobileProgramTitle}>Programs</h4>
            <div className={styles.mobileProgramList}>
              {programs.map((program) => (
                <Link
                  key={program.path}
                  to={program.path}
                  onClick={scrollToTop}
                  className={styles.mobileProgramItem}
                >
                  {program.name}
                </Link>
              ))}
            </div>
          </div>

          {/* Mobile Branch Contacts */}
          <div className={styles.mobileBranchSection}>
            <h4 className={styles.mobileBranchTitle}>
              <FiPhone /> Call Us
            </h4>
            <div className={styles.mobileBranchList}>
              {branches.map((branch) => (
                <a
                  key={branch.name}
                  href={`tel:${branch.phone.replace(/\s/g, '')}`}
                  className={styles.mobileBranchItem}
                >
                  <span className={styles.mobileBranchName}>{branch.name}</span>
                  <span className={styles.mobileBranchPhone}>{branch.phone}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </header>
  );
};

export default Header;